/**
 * The confirm-poll: a bounded, coalesced re-check that waits for an activity row to ARRIVE.
 *
 * ## Why a poll exists at all
 *
 * `activityRefreshStore.bump()` used to be enough, and its comment still says so: "by the time the
 * mutation API returns 200, the new activity row is already committed; the refetch will see it."
 * That was true of the direct `IActivityLogger` write. It is NOT true under the domain-event
 * consumer — the mutation commits a `DomainEvents` row, and the activity row is written LATER, by an
 * outbox consumer, as an async projection. An immediate refetch therefore returns *without* the row
 * the user just caused, and the panel looks broken precisely at the moment it is being watched.
 *
 * So the client re-checks a few times and stops the moment the row shows up.
 *
 * ## No timers in here
 *
 * Every function below is pure. The React layer owns `setTimeout` and its cleanup, which is what
 * makes the schedule and the stop condition testable in a Node runner with no DOM and no fake clock.
 */

/**
 * 400ms, 1.2s, 3s, 6s — then stop. At most FOUR extra requests spanning ~10.6s.
 *
 * Front-loaded because the consumer usually lands in well under a second, so the common case costs
 * one cheap request and ends; the long tail is covered by two waits, not by a tight loop. The cap is
 * the point: an unbounded poll on a panel that stays open all day is a background request generator.
 */
export const ARRIVAL_POLL_DELAYS_MS = [400, 1200, 3000, 6000] as const;

/**
 * How long to wait before poll attempt `attempt` (0-based), or `null` when the budget is spent.
 *
 * Anything that is not a valid index — negative, fractional, NaN — also returns `null`. A bounded
 * poll whose caller has lost count must STOP; the alternative failure mode is an infinite one.
 */
export function nextDelayMs(attempt: number): number | null {
  if (!Number.isInteger(attempt) || attempt < 0 || attempt >= ARRIVAL_POLL_DELAYS_MS.length) {
    return null;
  }
  return ARRIVAL_POLL_DELAYS_MS[attempt];
}

/** A position in the feed's sort order. Both halves — `occurredAtUtc` alone is not unique. */
export interface FeedMark {
  occurredAtUtc: string;
  id: number;
}

/**
 * Timestamp compare that does not assume a string format.
 *
 * `Date.parse` first, because the server has emitted both `Z` and `+00:00` suffixes and varying
 * fractional-second precision, none of which order correctly as raw text. Lexicographic compare is
 * the fallback for a value neither side can parse, which is a better answer than `NaN` silently
 * making every comparison false — the exact shape of failure this module exists to avoid.
 */
function compareTimestamps(a: string, b: string): number {
  const ta = Date.parse(a);
  const tb = Date.parse(b);

  if (Number.isNaN(ta) || Number.isNaN(tb)) {
    return a < b ? -1 : a > b ? 1 : 0;
  }

  return ta - tb;
}

/** `a` sorts strictly newer than `b` on (occurredAtUtc DESC, id DESC) — the feed's own order. */
export function isNewerThan(a: FeedMark, b: FeedMark): boolean {
  const byTime = compareTimestamps(a.occurredAtUtc, b.occurredAtUtc);
  if (byTime !== 0) return byTime > 0;
  return a.id > b.id;
}

/**
 * The newest (occurredAtUtc, id) in a list, or null for an empty one.
 *
 * Scans rather than reading `items[0]`: the feed arrives newest-first, but this is also called on a
 * list that has been appended to, filtered, or merged by {@link appendUnique}, and a sentinel taken
 * from a wrong assumption about order is a sentinel that never fires.
 */
export function highWaterMark(items: readonly FeedMark[]): FeedMark | null {
  let best: FeedMark | null = null;

  for (const item of items) {
    if (best === null || isNewerThan(item, best)) {
      best = { occurredAtUtc: item.occurredAtUtc, id: item.id };
    }
  }

  return best;
}

/**
 * Has anything arrived that was not already on screen?
 *
 * ⚠️ **THE SENTINEL IS THE FEED'S OWN HIGH-WATER MARK, NOT A WALL CLOCK.** An earlier draft compared
 * each row's `occurredAtUtc` against the time the client fired the refetch. That can never be true:
 * `occurredAtUtc` is FACT time, stamped inside the mutation's transaction, which necessarily
 * precedes the HTTP response, which precedes the client's version bump. Every row the poll was
 * waiting for is therefore older than the clock it was being compared to, so the condition never
 * fired and every single mutation burned the full four-request budget before giving up — a poll that
 * cost the maximum and provided the benefit of none. `arrivalPoll.test.ts` pins this with a case
 * built to fail under the wall-clock version.
 *
 * `seen === null` means nothing was on screen when the mutation ran (an empty feed, or a first
 * load), so any row at all is new.
 */
export function hasNewRow(seen: FeedMark | null, items: readonly FeedMark[]): boolean {
  if (seen === null) return items.length > 0;
  return items.some((item) => isNewerThan(item, seen));
}
